const post = {
  slug: "jasper-recall-local-rag-for-ai-agents",
  title: "Give Your AI Agent a Memory: Local RAG with Jasper Recall",
  contentType: "blog",
  excerpt: "AI agents wake up fresh every session. Jasper Recall gives them persistent memory using ChromaDB and local embeddings — no API keys required.",
  categories: ["AI", "OpenClaw", "Memory"],
  tags: ["rag", "chromadb", "embeddings", "memory", "ai-agent", "openclaw"],
  author: {
    name: "E. x .O. Team",
    email: "team@exohaven.online",
  },
  publishedAt: "2026-02-04",
  status: "published",
  featuredImage: "",
  content: `
# Give Your AI Agent a Memory: Local RAG with Jasper Recall

Every AI agent has the same problem: **they wake up fresh every session.**

Your agent helped you design an API yesterday. Today, it has no idea that conversation happened. You carefully explained your project architecture last week — gone. That critical decision about database schemas? Forgotten.

The solution isn't more context window. It's **retrieval-augmented generation (RAG)** — giving your agent the ability to search its own history and pull relevant memories into context on demand.

**Jasper Recall** is a local RAG system built for AI agents. It indexes your session logs and memory files into ChromaDB, embeds them with sentence-transformers, and lets your agent search semantically — all running locally, no API keys required.

---

## Why Local RAG Matters

### The Context Window Trap

You could dump your entire MEMORY.md into context every session. But context windows are finite and expensive. A 100KB memory file burns tokens on every single message, whether relevant or not.

RAG inverts this: **search first, then load only what's relevant**.

### Privacy-First Memory

Cloud RAG services see every memory you index. Local RAG keeps everything on your machine:

- ChromaDB stores vectors in \`~/.openclaw/chroma-db\`
- Embeddings run locally via sentence-transformers
- No data leaves your system

### Session Continuity

The magic happens when your agent learns to use recall as second nature:

\`\`\`
User: "What did we decide about the hopeIDS pricing?"

Agent: *runs recall "hopeIDS pricing"*
       *finds memory from 2 days ago*

"We decided on $19/mo for Pro tier or $29 one-time purchase.
The reasoning was to stay indie-friendly rather than enterprise pricing."
\`\`\`

Your agent doesn't just remember — it remembers *with context*.

---

## How It Works

Jasper Recall has three components that form a memory pipeline:

\`\`\`
┌────────────────┐     ┌──────────────┐     ┌───────────┐
│ Session Logs   │────▶│ digest-      │────▶│ Markdown  │
│ (.jsonl)       │     │ sessions     │     │ Summaries │
└────────────────┘     └──────────────┘     └─────┬─────┘
                                                  │
                                                  ▼
┌────────────────┐     ┌──────────────┐     ┌───────────┐
│ Memory Files   │────▶│ index-       │────▶│ ChromaDB  │
│ (*.md)         │     │ digests      │     │ Vectors   │
└────────────────┘     └──────────────┘     └─────┬─────┘
                                                  │
                                                  ▼
                       ┌──────────────┐     ┌───────────┐
                       │ recall       │◀────│ "query"   │
                       │              │     │           │
                       └──────────────┘     └───────────┘
\`\`\`

### 1. digest-sessions

Extracts key information from raw session logs:

- Topics discussed
- Tools used
- Decisions made

Converts verbose JSONL chat logs into searchable markdown summaries.

### 2. index-digests

Chunks markdown files and generates embeddings:

- Splits files into 500-char chunks with 100-char overlap
- Embeds using \`all-MiniLM-L6-v2\` (384 dimensions)
- Stores in ChromaDB with content hashing for deduplication

Only re-indexes files that have changed — incremental by default.

### 3. recall

Semantic search across your indexed memory:

\`\`\`bash
recall "API design decisions"     # Natural language query
recall "hopeIDS" --limit 10       # More results
recall "meeting" --json           # Structured output
recall "architecture" -v          # Show similarity scores
\`\`\`

---

## Quick Start

One command installs everything:

\`\`\`bash
npx jasper-recall setup
\`\`\`

This creates:
- Python venv with chromadb and sentence-transformers
- CLI scripts in \`~/.local/bin/\`
- ChromaDB database in \`~/.openclaw/chroma-db/\`

Then index your files:

\`\`\`bash
index-digests
\`\`\`

And search:

\`\`\`bash
recall "what we discussed yesterday"
\`\`\`

---

## Integration Patterns

### Memory-Augmented Responses

Teach your agent to recall before answering questions about past work:

\`\`\`markdown
## AGENTS.md

Before answering questions about prior work, decisions, or history:
1. Run \`recall "relevant keywords"\`
2. Include relevant context in your response
3. Cite the source file if helpful
\`\`\`

### Automated Maintenance

Add to HEARTBEAT.md for periodic memory maintenance:

\`\`\`markdown
## Memory Tasks
- [ ] New sessions since last digest? → \`digest-sessions\`
- [ ] Memory files updated? → \`index-digests\`
\`\`\`

### Cron Scheduling

For hands-off operation, schedule via OpenClaw cron:

\`\`\`json
{
  "schedule": { "kind": "cron", "expr": "0 */6 * * *" },
  "payload": {
    "kind": "agentTurn",
    "message": "Run index-digests to update memory index"
  },
  "sessionTarget": "isolated"
}
\`\`\`

---

## What Gets Indexed

By default, jasper-recall indexes markdown from your workspace:

| Directory | Content |
|-----------|---------|
| \`memory/*.md\` | Daily notes, MEMORY.md |
| \`memory/session-digests/\` | Session summaries |
| \`memory/repos/\` | Project documentation |
| \`memory/sops/\` | Standard operating procedures |
| \`memory/founder-logs/\` | Development logs |

Add more directories by setting \`RECALL_WORKSPACE\` or modifying \`index-digests\`.

---

## The Result

After a week of use, my agent Jasper has indexed:

- **21 files** across daily notes, project docs, and session digests
- **313 chunks** of searchable memory
- **~80MB** local embedding model (one-time download)

Queries return in **<100ms** after the model loads. The agent can now answer questions like:

- "What did we decide about the API architecture?"
- "When did we ship hopeIDS?"
- "What's the MongoDB connection string for moltbook?"

No context stuffing. No cloud APIs. Just local, semantic search over everything the agent has ever learned.

---

## Installation

**ClawHub (skill only):**
\`\`\`bash
clawhub install jasper-recall
\`\`\`

**npm (full package):**
\`\`\`bash
npx jasper-recall setup
\`\`\`

**GitHub:**
https://github.com/E-x-O-Entertainment-Studios-Inc/jasper-recall

---

## The Philosophy

Most AI memory solutions try to be clever — compressing memories, summarizing aggressively, using cloud services with complex APIs.

Jasper Recall is deliberately simple:

1. **Markdown is the source of truth** — human-readable, git-friendly
2. **Local embeddings** — no API costs, no privacy concerns
3. **Incremental indexing** — only process what changed
4. **Semantic search** — find relevant memories by meaning, not keywords

Your agent's memories should be as accessible to you as they are to the agent. No black boxes.

---

*Shipped Day 94 of the Founder Logs. Built in one evening, used every day since.*

🦊💜
`
};
export default post;
